import type { TFunction } from "i18next";
import type {
  TreatmentCardProps,
  TreatmentCardStatusRow,
} from "@noom/wax-component-library";

import { MedCtaAction } from "../nativeAnalytics/medAnalytics";
import { buildAuthenticatedUrlDeeplink } from "../../utils/urlUtils";

import { LabStatusIcon } from "./LabStatusIcon";
import {
  type GetTreatmentCardPropsHandlers,
  type TreatmentCardMapperOptions,
  formatDatedStatusText,
  getTreatmentBottomSheetConfig,
} from "./treatmentCardMapper";
import type { TreatmentOverview } from "./hooks/useTreatmentOverviews";
import { renderProgramName } from "./renderProgramName";
import { openDeeplink, openTrackingUrl } from "./utils";

type LabTreatmentOverview = Extract<
  TreatmentOverview,
  { __typename: "LabTreatmentOverview" }
>;

const getLabStatusRow = (
  treatment: LabTreatmentOverview,
  t: TFunction<"care">,
  handlers?: GetTreatmentCardPropsHandlers
): TreatmentCardStatusRow | undefined => {
  const { status } = treatment;

  if (!status) return undefined;

  const icon = <LabStatusIcon status={status.__typename} />;

  const withTrack = (
    text: string,
    trackingUrl: string | null | undefined
  ): TreatmentCardStatusRow => {
    if (!trackingUrl) {
      return { icon, text };
    }
    const label = t("status.cta.track");
    return {
      icon,
      text,
      label,
      onClick: () => {
        handlers?.onStatusRowCtaClick?.({
          ctaAction: MedCtaAction.TrackShipment,
          ctaText: label,
          medicationName: treatment.treatmentName,
        });
        openTrackingUrl(trackingUrl);
      },
    };
  };

  const withSeeResults = (
    text: string,
    resultsUrl: string | null | undefined
  ): TreatmentCardStatusRow => {
    if (!resultsUrl) {
      return { icon, text };
    }
    const label = t("status.cta.seeResults");
    return {
      icon,
      text,
      label,
      onClick: () => {
        handlers?.onStatusRowCtaClick?.({
          ctaAction: MedCtaAction.ViewResults,
          ctaText: label,
          medicationName: treatment.treatmentName,
        });
        openDeeplink(
          buildAuthenticatedUrlDeeplink(resultsUrl, {
            navContext: "modal",
            titleSource: "fromDocument",
          })
        );
      },
    };
  };

  switch (status.__typename) {
    case "TreatmentOverviewStatusShippingSoon":
      return { icon, text: t("status.shippingSoon") };
    case "TreatmentOverviewStatusShipped":
      return withTrack(
        formatDatedStatusText(
          t,
          status.shippedDate,
          "status.shipped",
          "status.shippedNoDate"
        ),
        status.trackingUrl
      );
    case "TreatmentOverviewStatusReplacementShipped":
      return withTrack(
        formatDatedStatusText(
          t,
          status.shippedDate,
          "status.replacementShipped",
          "status.replacementShippedNoDate"
        ),
        status.trackingUrl
      );
    case "TreatmentOverviewStatusCompletedKitShipped":
      return withTrack(t("status.completedKitShipped"), status.trackingUrl);
    case "TreatmentOverviewStatusCompletedKitReceived":
      return { icon, text: t("status.completedKitReceived") };
    case "TreatmentOverviewStatusResultsAvailable":
      return withSeeResults(t("status.resultsAvailable"), status.resultsUrl);
    case "TreatmentOverviewStatusDelivered":
      return { icon, text: t("status.delivered") };
    default:
      return undefined;
  }
};

export const getLabTreatmentCardProps = (
  treatment: LabTreatmentOverview,
  t: TFunction<"care">,
  handlers?: GetTreatmentCardPropsHandlers,
  options?: TreatmentCardMapperOptions
): TreatmentCardProps => {
  const onCardTapped = handlers?.onCardTapped;
  const onShowBottomSheet = handlers?.onShowBottomSheet;
  const onOpenTreatmentDetail = handlers?.onOpenTreatmentDetail;

  const hasSheet = !!getTreatmentBottomSheetConfig(treatment, t, options);
  const destination =
    options?.isBiomarkerTreatmentDetailEnabled &&
    onOpenTreatmentDetail &&
    treatment.treatmentCode
      ? () => onOpenTreatmentDetail(treatment)
      : hasSheet && onShowBottomSheet
        ? () => onShowBottomSheet(treatment)
        : treatment.deeplink
          ? () => openDeeplink(treatment.deeplink as string)
          : undefined;

  const cardOnClick = destination
    ? () => {
        onCardTapped?.(treatment);
        destination();
      }
    : undefined;

  return {
    programName: treatment.formattedProgramName
      ? renderProgramName(treatment.formattedProgramName)
      : undefined,
    title: treatment.treatmentName,
    imageBgUrl: treatment.productImageUrl,
    imageBgColor: treatment.productBackgroundColor ?? undefined,
    imageHeight: "85px",
    statusRow: getLabStatusRow(treatment, t, handlers),
    onClick: cardOnClick,
  };
};
